<template>
  <Dialog
    :open="isModalOpen"
    class="fixed inset-0 z-50 py-6 overflow-auto bg-white sm:bg-transparent sm:p-6 sm:p-12"
    @close-modal="closeModal"
    @click.self="closeModal"
  >
    <div
      class="max-w-4xl px-6 mx-auto bg-white sm:px-16 md:px-24 sm:py-10 sm:rounded-lg sm:shadow z-50 relative"
    >
      <div class="absolute xs:top-1r sm:top-1r md:top-1r lg:top-1r right-1r">
        <button
          class="h-6 w-6 flex items-center justify-center"
          @click="closeModal"
        >
          <span class="sr-only">Close</span>
          <CloseIcon />
        </button>
      </div>
      <DialogPanel>
        <DialogTitle
          class="flex justify-center mt-6 text-xl leading-tight text-center sm:text-2xl md:text-3xl my-10"
        >
          Block Room
        </DialogTitle>
        <div class="flex flex-col justify-center mt-14">
          <div class="flex items-center justify-center">
            <div class="flex flex-col mr-5">
              <div class="flex items-center ml-2">
                <span class="text-red-500 mr-2">*</span>
                <label for="start_date" class="font-semibold">Start Date</label>
              </div>
              <Calendar
                v-model="data.start_date"
                class="w-52 mx-2 h-10 border-gray-300"
                :input-class="[
                  data.start_date ? 'border-gray-300' : 'border-red-100',
                ]"
                panel-class="bg-white border-gray-300"
              />
            </div>
            <div class="flex flex-col mr-5">
              <div class="flex items-center ml-2">
                <span class="text-red-500 mr-2">*</span>

                <label for="end_date" class="font-semibold">End Date</label>
              </div>
              <Calendar
                v-model="data.end_date"
                class="w-52 mx-2 h-10 border-gray-300"
                :input-class="[
                  data.end_date && data.end_date > data.start_date
                    ? 'border-gray-300'
                    : 'border-red-100',
                ]"
                panel-class="bg-white border-gray-300"
              />
            </div>
            <BasicTextInput
              v-model="data.sublessee_name"
              class="rounded-md border-gray-300"
              outer-classes="w-60 mx-2 my-2"
              type="text"
            >
              <template #fieldLabel>
                <span class="font-semibold">Sublessee Name</span>
              </template>
            </BasicTextInput>
          </div>
          <div class="flex mt-12 align-center justify-center h-10 flex-0">
            <BaseButton class="mr-10" outlined @click.self="closeModal">
              Cancel
            </BaseButton>
            <BaseButton @click.self="submitBlockRoom">
              Submit
            </BaseButton>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup>
import { computed, defineEmits, reactive, toRefs } from "vue";
import Calendar from "primevue/calendar";
import { useToast } from "vue-toast-notification";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import { ERROR_TIMEOUT } from "@/constants";
import { areDatesIntervalConflicting } from "@/util/date";
import { parseISO, format as formatDate } from "date-fns";
import { helpers, minValue, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { TOAST_MESSAGES } from "@/components/StudentFulfillment/components/report/TableauEventDetail/constants";

const props = defineProps({
  isModalOpen: {
    type: Boolean,
    default: false,
  },
  dateRangesWithBookings: {
    type: Array,
    default: () => [],
  },
});

const { isModalOpen, dateRangesWithBookings } = toRefs(props);

const data = reactive({
  start_date: undefined,
  end_date: undefined,
  sublessee_name: undefined,
});

const rules = {
  start_date: {
    required: helpers.withMessage("Start date is required", required),
    minValue: helpers.withMessage(
      "Start date must be greater than today",
      minValue(new Date())
    ),
  },
  end_date: {
    required: helpers.withMessage("End date is required", required),
    minValue: helpers.withMessage(
      "End date must be greater than today",
      minValue(new Date())
    ),
    isAfterStartDate: helpers.withMessage(
      "End date must be after start date",
      (value) => value > data.start_date
    ),
  },
};

const v$ = useVuelidate(rules, data);
const emit = defineEmits(["onCancel", "onContinue"]);
const toast = useToast();
const closeModal = () => {
  emit("onCancel");
};

const checkConflicts = computed(() => {
  return (
    dateRangesWithBookings.value?.reduce(
      (acc, curr) =>
        acc ||
        areDatesIntervalConflicting(
          new Date(parseISO(curr.booking_start_date)),
          new Date(parseISO(curr.booking_end_date)),
          data.start_date,
          data.end_date
        ),
      false
    ) ?? false
  );
});

const submitBlockRoom = async () => {
  v$.value.$reset();
  const validator = await v$.value.$validate();
  if (!validator) {
    const errorMessage = v$.value.$errors
      ?.map(({ $message }) => $message)
      ?.join(" and ");
    toast.open({
      message: errorMessage,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }
  if (checkConflicts.value) {
    toast.open({
      message: TOAST_MESSAGES.dates_conflicting_message,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }
  const dataToSubmit = {
    booking_start_date: formatDate(data.start_date, "yyyy-MM-dd"),
    booking_end_date: formatDate(data.end_date, "yyyy-MM-dd"),
    sublessee_name: data.sublessee_name,
  };
  emit("onContinue", dataToSubmit);
};
</script>
